import * as React from "react";
import {
  Box,
  Dialog,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { useTranslation } from "react-i18next";

const splitExtension = (filename) => {
  const name = filename || "";
  const lastDot = name.lastIndexOf(".");
  if (lastDot <= 0) return { base: name, extension: "" };
  return { base: name.slice(0, lastDot), extension: name.slice(lastDot) };
};

// Shared full-size media preview dialog — same look everywhere a media item
// (image or video) can be opened for a closer look. `canRename` gates the
// rename affordance entirely; pass `onRename` (async, may throw to keep the
// field open for a retry) only where renaming is actually allowed — e.g. the
// Media library screen, not read-only consumers like the Carousel preview.
const MediaPreviewDialog = ({ preview, onClose, canRename = false, onRename }) => {
  const { t } = useTranslation();
  const [renaming, setRenaming] = React.useState(false);
  const [renameValue, setRenameValue] = React.useState("");
  const [renameExtension, setRenameExtension] = React.useState("");
  const [savingRename, setSavingRename] = React.useState(false);

  React.useEffect(() => {
    setRenaming(false);
  }, [preview]);

  const startRenaming = () => {
    const { base, extension } = splitExtension(preview?.filename);
    setRenameValue(base);
    setRenameExtension(extension);
    setRenaming(true);
  };

  const cancelRenaming = () => setRenaming(false);

  const saveRenaming = async () => {
    const trimmedBase = renameValue.trim();
    const trimmed = `${trimmedBase}${renameExtension}`;
    if (!trimmedBase || trimmed === preview?.filename) {
      setRenaming(false);
      return;
    }
    setSavingRename(true);
    try {
      await onRename(trimmed);
      setRenaming(false);
    } catch {
      // Keep the field open so the user can retry; the caller is
      // responsible for surfacing the error (e.g. a toast).
    } finally {
      setSavingRename(false);
    }
  };

  return (
    <Dialog
      open={Boolean(preview)}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { bgcolor: "background.paper", overflow: "hidden" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        {renaming ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
            <TextField
              autoFocus
              variant="outlined"
              size="small"
              fullWidth
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveRenaming();
                if (e.key === "Escape") cancelRenaming();
              }}
              disabled={savingRename}
              InputProps={{
                endAdornment: renameExtension ? (
                  <InputAdornment position="end" sx={{ ml: 0 }}>
                    {renameExtension}
                  </InputAdornment>
                ) : undefined,
              }}
            />
            <Tooltip title={t("common.save")}>
              <span>
                <IconButton
                  size="small"
                  color="success"
                  onClick={saveRenaming}
                  disabled={savingRename}
                >
                  <CheckIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t("common.cancel")}>
              <span>
                <IconButton size="small" onClick={cancelRenaming} disabled={savingRename}>
                  <CloseIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ) : (
          <>
            <Typography variant="h5" fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>
              {preview?.filename || t("media.untitled")}
            </Typography>
            {canRename && (
              <Tooltip title={t("media.rename")}>
                <IconButton onClick={startRenaming} size="small">
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </>
        )}
      </Box>
      <Box
        sx={{
          minHeight: 280,
          maxHeight: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "action.hover",
          p: 2,
        }}
      >
        {preview?.video ? (
          <video
            src={preview.src}
            controls
            autoPlay
            style={{ maxWidth: "100%", maxHeight: "65vh" }}
          />
        ) : (
          <Box
            component="img"
            src={preview?.src}
            alt={preview?.filename || t("media.untitled")}
            sx={{ maxWidth: "100%", maxHeight: "65vh", objectFit: "contain" }}
          />
        )}
      </Box>
    </Dialog>
  );
};

export default MediaPreviewDialog;
